import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useCallback, useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { useLogger, ANALYTICS_EVENTS } from "@/features/analytics"

import { advisorsList } from "../config/advisors"
import type { Advisor } from "../types"

/**
 * Advisor interaction handlers, ported from the source Advisors.tsx orchestrator.
 * Owns the out-of-credits paywall state.
 */
export function useAdvisorActions() {
  const navigate = useNavigate()
  const { user, userCreditsCount, setSelectedAdvisor, setPreSelectedChatMessage } = useChat()
  const { trackAppyCntEvent } = useLogger()
  const [isPaywallOpen, setIsPaywallOpen] = useState(false)

  const userPayload = useCallback(
    () => ({ uid: user?.uid, name: user?.displayName, host: window.location.host }),
    [user],
  )

  const handleChatWithAdvisor = useCallback(
    (advisor: Advisor) => {
      setSelectedAdvisor(advisor)
      if (userCreditsCount === 0) {
        setIsPaywallOpen(true)
        trackAppyCntEvent(ANALYTICS_EVENTS.PAYWALL_SHOW, undefined, { user: userPayload() })
      } else {
        navigate({ to: WebappRoutes.READINGS })
      }
    },
    [userCreditsCount, setSelectedAdvisor, trackAppyCntEvent, userPayload, navigate],
  )

  // Opens the advisor detail page, passing which advisor via the `advisorId` search param.
  const handleAdvisorInner = useCallback(
    (advisor: Advisor) => {
      navigate({ to: WebappRoutes.ADVISOR_INNER, search: { advisorId: advisor.assistantId } })
    },
    [navigate],
  )

  const handleChatWithPreselectedQuestion = useCallback(
    (preset: string) => {
      setPreSelectedChatMessage(preset)
      setSelectedAdvisor(advisorsList[0])

      if (userCreditsCount === 0) {
        setIsPaywallOpen(true)
        trackAppyCntEvent(ANALYTICS_EVENTS.PAYWALL_SHOW, undefined, { user: userPayload() })
      } else {
        navigate({ to: WebappRoutes.READINGS })
        trackAppyCntEvent(ANALYTICS_EVENTS.CHAT_WITH_ADVISOR_AND_PRESET, undefined, {
          user: userPayload(),
          advisor: {
            name: advisorsList[0].name,
            id: advisorsList[0].assistantId,
            chat_type: "Chat with Advisor & Preset",
            preset,
          },
        })
      }
    },
    [
      userCreditsCount,
      setPreSelectedChatMessage,
      setSelectedAdvisor,
      trackAppyCntEvent,
      userPayload,
      navigate,
    ],
  )

  const closePaywall = useCallback(() => {
    setPreSelectedChatMessage(null)
    setSelectedAdvisor(null)
    setIsPaywallOpen(false)
  }, [setPreSelectedChatMessage, setSelectedAdvisor])

  return {
    isPaywallOpen,
    closePaywall,
    handleChatWithAdvisor,
    handleAdvisorInner,
    handleChatWithPreselectedQuestion,
  }
}
